import React, { useEffect } from 'react'
import { navigate } from 'gatsby'
import styled from 'styled-components'

import {
  Box,
  Button,
  Paragraph,
  P,
  H1,
  H2,
  H3,
  ButtonUnstyled,
  Tile,
  IconContainer,
} from '@allied-solutions/affinity'
import { SuccessSolid } from '@allied-solutions/affinity-react-icons/dist/standard'

import { formatDate } from '../functions'
import {
  usePageData,
  useRedirectToEnterRefId,
  useStore,
  useWindowWidth,
  useLogTransaction,
  useCiamAuthorize,
} from '../hooks'
import { theme } from '../theme.js'
import { AppLayout, InfoBox } from './AppLayout'
import { ButtonGroup } from './ButtonGroup'
import { SubmitInsuranceLayout } from './SubmitInsuranceLayout'

const StyledTile = styled(Tile)`
  border-radius: ${props => props.theme.radii.xl};
`

const Confirmation = ({ location, data }) => {
  const { dispatch, state } = useStore()
  useRedirectToEnterRefId()
  const { workflowType } = usePageData(location)
  const windowWidth = useWindowWidth()
  const largeBreakpoint = theme[3].replace('px', '')
  const { LogTransaction } = useLogTransaction()
  const { handleAuthorize } = useCiamAuthorize()

  useEffect(() => {
    Object.values(location.state).forEach(async logEvent => {
      if (logEvent?.Event)
        await LogTransaction({
          type: logEvent?.Event,
          files: logEvent?.files,
        })
    })
  }, [location.state])

  return (
    // utilizing 'main' here causes duplication of landmark use, SubmitInsuranceLayout utilzes main in the Helment
    <SubmitInsuranceLayout location={location} disableBeforeUnload={true}>
      {/* <main> */}
      <AppLayout
        leftSide={
          <>
            <Paragraph
              mb={6}
              typeStyle="labelLarge"
              textAlign="left"
              color="gray600"
            >
              Thank you!
            </Paragraph>
            <H1 className="h4" typeStyle="headlineLarge.black">
              Create a Profile!
            </H1>
            <P
              typeStyle="bodyMedium"
              color="text.secondary"
              mt="large"
              mb="5xl"
            >
              Create a profile to check your submission status in the future. If
              you do not want to create a profile, you may close this window.
            </P>
            <StyledTile>
              <Tile.Header>
                <H2 typeStyle="titleSmall.bold" m="0" color="text.default">
                  Benefits of a profile
                </H2>
              </Tile.Header>
              <Box
                display="flex"
                gap={8}
                flexDirection="column"
                color="text.secondary"
                pr={[2, 0, null, null]}
              >
                <Box display="flex" gap={3}>
                  <IconContainer icon={SuccessSolid} color="green" />
                  <P typeStyle="bodyMedium" mb="0">
                    Faster access to submission status without needing to call.
                  </P>
                </Box>
                <Box display="flex" gap={3}>
                  <IconContainer icon={SuccessSolid} color="green" />
                  <P typeStyle="bodyMedium" mb="0">
                    Detailed next steps if more information is needed.
                  </P>
                </Box>
                <Box display="flex" gap={3}>
                  <IconContainer icon={SuccessSolid} color="green" />
                  <P typeStyle="bodyMedium" mb="0">
                    Easily review your loan details.
                  </P>
                </Box>
              </Box>
            </StyledTile>
            <Box
              backgroundColor="background.brandMuted"
              p="lg"
              borderRadius="md"
              mt="5xl"
            >
              <Paragraph typeStyle="inputXsmall" mb="0">
                We don't sell your data. View our{' '}
                <Paragraph
                  as="a"
                  href="/privacy-policy"
                  typeStyle="buttonSmall"
                  color="text.brand"
                  textDecoration="none"
                >
                  privacy policy here.
                </Paragraph>
              </Paragraph>
            </Box>
          </>
        }
        rightSide={
          <InfoBox>
            {(state.borrower.ReferenceID ||
              workflowType === 'other' ||
              workflowType === 'noReferenceId') && (
              <>
                <Box display="flex" alignItems="center" gap={3}>
                  <H3
                    className="titleSmall"
                    typeStyle="titleSmall"
                    fontWeight={900}
                    textAlign="left"
                    mb={0}
                  >
                    Confirmation
                  </H3>
                  <P
                    display={['block', 'block', 'block', 'inline-block']}
                    ml={[null, '3']}
                    mb={0}
                    typeStyle="caption"
                    textAlign="left"
                    color="hint"
                  >
                    Submitted on {formatDate(state.borrower.Date)}
                  </P>
                </Box>
                {data}
              </>
            )}
          </InfoBox>
        }
        footerButtons={
          <ButtonGroup display="flex" justifyContent="flex-end" gap={6}>
            <ButtonUnstyled
              id="SubmitInsurance__Confirm__Button--close"
              color="text.secondary"
              typeStyle="buttonMedium"
              size="md"
              mr={['0', '4xl']}
              onClick={() => {
                dispatch({ type: 'resetForm' })
                navigate('/')
              }}
            >
              Close
            </ButtonUnstyled>
            {windowWidth < largeBreakpoint ? null : (
              <Button
                rounded
                outlined
                id="SubmitInsurance__Confirm__Button--print"
                variant="gray"
                onClick={() => window.print()}
              >
                Print Confirmation
              </Button>
            )}
            <Button
              rounded
              onClick={() => {
                handleAuthorize()
              }}
            >
              Create a Profile
            </Button>
          </ButtonGroup>
        }
      />
      {/* </main> */}
    </SubmitInsuranceLayout>
  )
}

export { Confirmation }
